<template>
  <div class="page">
    <n-loading-spinner v-if="loading"></n-loading-spinner>

    <section class="section content-container" v-if="!loading">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column">
            <div class="island">
              <nav class="level">
                <div class="level-left"></div>
                <div class="level-right">
                  <p class="level-item">
                    <n-button id="add-answer-button" button @click="addAnswer()" variant="primary">
                      <span>Add FAQ</span>
                    </n-button>
                  </p>
                </div>
              </nav>

              <table class="table is-fullwidth">
                <tr>
                  <th>Frequently Asked Question</th>
                  <th class="status-column">Status</th>
                  <th>&nbsp;</th>
                </tr>

                <tr class="answer-row" v-for="answer in answers" :key="answer.id">
                  <td>
                    <router-link :to="{ name: 'bots-answer-editor', params: { id: answer.id } }">{{
                      answer.title
                    }}</router-link>
                  </td>
                  <td>
                    <n-badge v-if="answer.status === 'live'" variant="success">Live</n-badge>
                    <n-badge v-if="answer.status === 'paused'" variant="secondary">Paused</n-badge>
                    <n-badge v-if="answer.status === 'draft'" variant="default">Draft</n-badge>
                  </td>
                  <td id="drop-down-menu" class="menu-icon action">
                    <DropDownMenu :go-left="true" dropDownIcon="dots">
                      <a @click="deleteAnswer(answer)" class="dropdown-item">
                        <DeleteIcon />
                        Delete
                      </a>
                    </DropDownMenu>
                  </td>
                </tr>
              </table>

              <ConfirmationModal
                id="delete-modal"
                v-if="showDeleteModal"
                confirmationTitle="Delete FAQ"
                :confirmationMessage="getDeleteAnswerConfirmationMessage()"
                :typeConfirmationValidationMessage="answerToDelete.title"
                confirmButtonLabel="Delete"
                @confirm="deleteAnswerConfirm()"
                @cancel="showDeleteModal = false"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <AddAnswerModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @answer-added="(answer: AnswerViewModel) => editAnswer(answer, true)"
    />
  </div>
</template>

<script lang="ts" setup>
import { answersApi } from '../services/answers';
import { AnswerListItemViewModel } from '../models/answers/answerListItemViewModel';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import Toast from '@/shared/services/toast';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import { LOAD_ANSWERS } from '@/store/answers/actionTypes';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import AddAnswerModal from '@/features/bots/components/AddAnswerModal.vue';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { AnswerViewModel } from '@/features/bots/models/answers/answerViewModel';

const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const loadAnswers = (forceLoad: boolean = false) => store.dispatch(LOAD_ANSWERS, forceLoad);

const answers = ref<AnswerListItemViewModel[]>([]);
const loading = ref(true);
const showCreateModal = ref(false);
const showDeleteModal = ref(false);
const answerToDelete = ref<AnswerListItemViewModel>();

onMounted(async () => {
  await getAnswers();
  loading.value = false;
});

function addAnswer() {
  showCreateModal.value = true;
}

function editAnswer(answer: AnswerListItemViewModel, created = false) {
  router.push({ name: 'bots-answer-editor', params: { id: answer.id }, query: { created: created.toString() } });
}

function deleteAnswer(answer: AnswerListItemViewModel) {
  answerToDelete.value = answer;
  showDeleteModal.value = true;
}

async function deleteAnswerConfirm() {
  if (!answerToDelete.value) return;

  showDeleteModal.value = false;
  try {
    await answersApi.deleteAnswer(currentWorkspace.value.id, answerToDelete.value.id);
    answers.value = answers.value.filter((answer) => answer.id !== answerToDelete.value?.id);
    Toast.showSuccess('Deleted FAQ');
  } catch (ex) {
    Toast.showError('Failed to delete FAQ : ' + ex.message);
  }
}

function getDeleteAnswerConfirmationMessage() {
  return `Are you sure you want to delete the <b>${HtmlFormatter.escapeHtmlCharacters(answerToDelete.value?.title || '')}</b> FAQ? The action is irreversible. Please type the name of the FAQ to confirm:`;
}

async function getAnswers() {
  answers.value = await loadAnswers(true);
}
</script>
<style scoped lang="scss">
.menu-icon {
  cursor: pointer;
}

table {
  td,
  th {
    padding: 5px;
  }

  td {
    vertical-align: middle;
    &.action {
      .dropdown {
        float: right;
      }
    }
  }
}

.status-column {
  width: 5em;
}

.questions-column {
  width: 10em;
}

#drop-down-menu {
  span {
    padding-right: 30px;
  }
}
</style>
